import { FileRepository } from "@api/repositories/FileRepository";
import { parseJson } from "@common/TypesHelper";
import { getImageUrl } from "@common/ImageUrlHelper";
import Vue from "vue";
import { ArrayHelpers } from "@common/ArrayHelpers";
import { getFileSizeLimit, validateFilesExt, validateFileSize } from "@common/fileInputHelper";
import i18n from "@service/locale/i18";

export interface ImageState {
    id: string;
    url?: string;
    error?: string;
    success?: boolean;
    onLoad?: boolean;
    active?: boolean;
    file?: File;
    isInvalid?: boolean;
}

export class BackgroundImageService {

    images: Record<string, ImageState> = Vue.observable({});
    restoredImages: Record<string, ImageState> = Vue.observable({});

    defaultFileSizeLimit = 3;

    constructor(
        private fileRepository: FileRepository,
        private key: string
    ) {

    }

    /**
     * Add file, validate and upload it
     * @param file
     * @param backend
     * @returns
     */
    async add(file: File, backend: string) {
        const localUrl = window.URL.createObjectURL(file);
        const localFileId = localUrl.split("/")[3];

        let {message, validatedFileUrl } = this.validate(file, localUrl);
        let valid = !message;

        if(valid) {
            const localImage = Vue.set<ImageState>(this.images, localFileId, { id: localFileId, url: localUrl, onLoad: true });
            return await this.uploadImage(file, localImage, backend);
        }
        else {
            Vue.set<ImageState>(this.images, localFileId, { id: localFileId, url: validatedFileUrl, isInvalid: true, error: message })
        }
    }

    /**
     * Creates image on server and uploads it to server
     * Deletes local image from image list
     * @param file
     * @param newImage
     * @param backend
     * @returns
     */
    async uploadImage(file: File, localImage: ImageState, backend: string) {
        try {
            const createdFile = await this.createFile(file);

            if (createdFile.id) {
                //replace image with new id from the server
                Vue.delete(this.images, localImage.id);

                const image = Vue.set<ImageState>(this.images, createdFile.id, { id: createdFile.id, url: getImageUrl(backend, createdFile.id), success: true });

                setTimeout(() => {
                    image.success = undefined;
                }, 1000);

                this.store(createdFile.id);

                return image;
            }
        }
        catch (e) {
            localImage.onLoad = false;
            localImage.error = i18n.t('web-conferences.backgroundSelect.connectionError').toString();
            localImage.file = file;

            throw new Error("While image upload occured the error")
        }
    }

    async retryUpload(image: ImageState, backend: string) {
        if (image.file) {
            image.onLoad = true;

            return await this.uploadImage(image.file, image, backend);
        }
        else {
            throw new Error("image file must be set for retry")
        }
    }

    /**
     * add new file id to Localstorage
     * @param id
     */
    private store(id: string) {

        const jsonString = localStorage.getItem(this.key);

        let urlList = parseJson(jsonString);

        if(urlList && Array.isArray(urlList)) {
            urlList.push(id);
        }
        else {
            urlList = [id];
        }

        localStorage.setItem(this.key, JSON.stringify(urlList));
    }

    restore(backend: string) {

        //restore images
        //we need look file ids in localstorage
        //if there are then we need created object with urls of this images

        //we need to get list from LS
        //and create imagesStates by this ids

        const jsonString = localStorage.getItem(this.key);
        let urlList = parseJson(jsonString) as string[] | null;

        const images = urlList?.map<Omit<ImageState, 'id'> & { id: string }>(id => {
            return {
                id: id,
                url: getImageUrl(backend, id)
            }
        }) || [];

        Vue.set(this, "restoredImages", ArrayHelpers.keyBy(images, "id"));
    }

    async createFile(file: File) {
        return await this.fileRepository.create(file, file.name);
    }

    async deleteFile(image: ImageState) {
        if (image.id) {
            //revoke image
            image.url && window.URL.revokeObjectURL(image.url);

            const jsonString = localStorage.getItem(this.key);

            let urlList = parseJson(jsonString);

            if (urlList && Array.isArray(urlList)) {
                urlList = urlList.filter((storageId) => storageId !== image.id)
            }

            localStorage.setItem(this.key, JSON.stringify(urlList));

            // remove from the list
            Vue.delete(this.images, image.id);
            Vue.delete(this.restoredImages, image.id);

            //delete from server
            await this.fileRepository.delete(image.id);
        }
        else {
            throw new Error("image id is not defined")
        }
    }

    /**
     * Validate uploaded file
     * @param file
     * @param url
     * @returns
     */
    private validate(file: File, url: string) {
        let message = "";

        const validated = validateFilesExt([file], ['jpg', 'jpeg', 'png']);
        if (validated.invalid.length) {
            window.URL.revokeObjectURL(url);
            url = "";
            message = i18n.t('web-conferences.backgroundSelect.extensionError').toString();
        }

        if (!validateFileSize(file, this.fileSizeLimit)) {
            message = i18n.t('web-conferences.backgroundSelect.sizeError').toString();
        }

        return { message, validatedFileUrl: url };
    }

    get fileSizeLimit() {
        return getFileSizeLimit(this.defaultFileSizeLimit);
    }

    destroy() {
        const object = { ...this.images, ...this.restoredImages };

        for (const key in object) {
            if (Object.prototype.hasOwnProperty.call(object, key)) {
                const image = object[key];

                if(!image.active) {
                    const url = image.url;
                    url && window.URL.revokeObjectURL(url);
                }
            }
        }
    }
}